import {announce} from '@github-ui/aria-live'
import type {EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import {getStartupConnectingMessage} from '../ahp/task-startup-store'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react'

type SessionLoadingSourceState = {state: 'idle' | 'pending'} | {state: 'error'; replacesLoading: boolean}

export type SessionLoadingSource =
  | ({kind: 'lookup' | 'history'} & SessionLoadingSourceState)
  | ({
      kind: 'preparation'
      phase?: 'preparing' | 'cloning'
      progress?: number
      launchKind?: EnvironmentKind
    } & SessionLoadingSourceState)
  | ({kind: 'connection'; phase?: 'waking' | 'connecting'} & SessionLoadingSourceState)

export interface SessionLoadingPresentation {
  descriptionId: string
  kind: SessionLoadingSource['kind']
  message: string
  slow: boolean
  progress?: number
}

interface SessionLoadingReportContextValue {
  displayKey: string
  visible: boolean
  acceptsChildDisplayKey?: boolean
  renderSources?: SessionLoadingSource[]
  report: (
    owner: symbol,
    displayKey: string,
    source: SessionLoadingSource | undefined,
    visible: boolean,
    announcementTargetRef?: RefObject<HTMLElement | null>,
  ) => void
}

const SessionLoadingReportContext = createContext<SessionLoadingReportContextValue | null>(null)
const SessionLoadingPresentationContext = createContext<{
  descriptionId: string
  ownsFallback: boolean
  presentation?: SessionLoadingPresentation
} | null>(null)
const EMPTY_SOURCES: readonly SessionLoadingSource[] = []
const SLOW_WARNING_DELAY_MS = 10_001
const CONTINUITY_IDLE_DISPLAY_KEY = 'session-loading-continuity-idle'
const subscribeToHydration = () => () => {}
const getClientRenderSnapshot = () => false
const getServerRenderSnapshot = () => true

function useRenderSourcesDuringHydration() {
  return useSyncExternalStore(subscribeToHydration, getClientRenderSnapshot, getServerRenderSnapshot)
}

function sourceMessage(source: SessionLoadingSource, sources: readonly SessionLoadingSource[]): string {
  switch (source.kind) {
    case 'preparation':
      if (source.phase === 'cloning') return 'Cloning repository…'
      if (source.phase === 'preparing') return 'Preparing repository…'
      return getStartupConnectingMessage(
        sources.some(
          candidate =>
            candidate.kind === 'preparation' &&
            candidate.state === 'pending' &&
            candidate.phase === undefined &&
            candidate.launchKind === 'managed-sandbox',
        )
          ? 'managed-sandbox'
          : undefined,
      )
    case 'connection':
      return 'Connecting…'
    case 'lookup':
      return 'Opening previous chat…'
    case 'history':
      return 'Loading…'
  }
}

function selectSource(sources: readonly SessionLoadingSource[]): SessionLoadingSource | undefined {
  const pending = sources.filter(source => source.state === 'pending')
  return (
    pending.find(source => source.kind === 'preparation' && source.phase !== undefined) ??
    pending.find(
      source => source.kind === 'preparation' && source.phase === undefined && source.launchKind === 'managed-sandbox',
    ) ??
    pending.find(source => source.kind === 'preparation') ??
    pending.find(source => source.kind === 'connection') ??
    pending.find(source => source.kind === 'lookup') ??
    pending.find(source => source.kind === 'history')
  )
}

function selectReportedSource(sources: readonly SessionLoadingSource[]): SessionLoadingSource | undefined {
  return (
    sources.find(source => source.state === 'error' && source.replacesLoading) ??
    selectSource(sources) ??
    sources.find(source => source.state === 'error')
  )
}

function sourceEqual(left: SessionLoadingSource | undefined, right: SessionLoadingSource | undefined): boolean {
  if (!left || !right) return left === right
  return (
    left.kind === right.kind &&
    left.state === right.state &&
    (left.state !== 'error' || right.state !== 'error' || left.replacesLoading === right.replacesLoading) &&
    (left.kind !== 'preparation' ||
      right.kind !== 'preparation' ||
      (left.phase === right.phase &&
        Object.is(left.progress, right.progress) &&
        left.launchKind === right.launchKind)) &&
    (left.kind !== 'connection' || right.kind !== 'connection' || left.phase === right.phase)
  )
}

export function SessionLoadingBoundary({
  displayKey,
  visible,
  sources = EMPTY_SOURCES,
  announcementTargetRef,
  children,
}: {
  displayKey: string
  visible: boolean
  sources?: readonly SessionLoadingSource[]
  announcementTargetRef?: RefObject<HTMLElement | null>
  children: ReactNode
}) {
  const parent = use(SessionLoadingReportContext)
  if (parent) {
    return (
      <NestedSessionLoadingBoundary
        parent={parent}
        displayKey={displayKey}
        visible={visible}
        sources={sources}
        announcementTargetRef={announcementTargetRef}
      >
        {children}
      </NestedSessionLoadingBoundary>
    )
  }

  return (
    <SessionLoadingBoundaryRoot
      displayKey={displayKey}
      visible={visible}
      sources={sources}
      announcementTargetRef={announcementTargetRef}
    >
      {children}
    </SessionLoadingBoundaryRoot>
  )
}

export function SessionLoadingPresentationScope({children}: {children: ReactNode}) {
  const renderSourcesDuringHydration = useRenderSourcesDuringHydration()
  const [reported, setReported] = useState<{
    displayKey: string
    sources: Map<
      symbol,
      {
        displayKey: string
        source: SessionLoadingSource
        visible: boolean
        announcementTargetRef?: RefObject<HTMLElement | null>
      }
    >
  }>(() => ({displayKey: CONTINUITY_IDLE_DISPLAY_KEY, sources: new Map()}))

  const report = useCallback(
    (
      owner: symbol,
      displayKey: string,
      source: SessionLoadingSource | undefined,
      visible: boolean,
      announcementTargetRef?: RefObject<HTMLElement | null>,
    ) => {
      setReported(current => {
        const existing = current.sources.get(owner)
        if (!source) {
          if (!existing || existing.displayKey !== displayKey) return current
          const next = new Map(current.sources)
          next.delete(owner)
          return {...current, sources: next}
        }

        if (current.displayKey !== displayKey) {
          return {
            displayKey,
            sources: new Map([[owner, {displayKey, source, visible, announcementTargetRef}]]),
          }
        }
        if (
          existing?.displayKey === displayKey &&
          sourceEqual(existing.source, source) &&
          existing.visible === visible &&
          existing.announcementTargetRef === announcementTargetRef
        ) {
          return current
        }
        const next = new Map(current.sources)
        next.set(owner, {displayKey, source, visible, announcementTargetRef})
        return {...current, sources: next}
      })
    },
    [],
  )

  const sources: SessionLoadingSource[] = []
  const visibleSources: SessionLoadingSource[] = []
  let announcementTargetRef: RefObject<HTMLElement | null> | undefined
  for (const reportedSource of reported.sources.values()) {
    if (reportedSource.displayKey !== reported.displayKey) continue
    sources.push(reportedSource.source)
    if (reportedSource.visible) {
      visibleSources.push(reportedSource.source)
      announcementTargetRef = reportedSource.announcementTargetRef ?? announcementTargetRef
    }
  }
  const stableReportValue = useMemo<SessionLoadingReportContextValue>(
    () => ({displayKey: reported.displayKey, visible: true, acceptsChildDisplayKey: true, report}),
    [report, reported.displayKey],
  )
  const reportValue = useMemo<SessionLoadingReportContextValue>(
    () => (renderSourcesDuringHydration ? {...stableReportValue, renderSources: []} : stableReportValue),
    [renderSourcesDuringHydration, stableReportValue],
  )

  return (
    <SessionLoadingReportContext value={reportValue}>
      <SessionLoadingPresentationOwner
        displayKey={reported.displayKey}
        visible={Boolean(selectSource(visibleSources))}
        sources={sources}
        presentationSources={visibleSources}
        announcementTargetRef={announcementTargetRef}
      >
        {children}
      </SessionLoadingPresentationOwner>
    </SessionLoadingReportContext>
  )
}

function NestedSessionLoadingBoundary({
  parent,
  displayKey,
  visible,
  sources,
  announcementTargetRef,
  children,
}: {
  parent: SessionLoadingReportContextValue
  displayKey: string
  visible: boolean
  sources: readonly SessionLoadingSource[]
  announcementTargetRef?: RefObject<HTMLElement | null>
  children: ReactNode
}) {
  const [owner] = useState(() => Symbol('nested-session-loading-boundary'))
  const effectiveVisible = parent.visible && visible
  const selectedSource = selectReportedSource(sources)
  const kind = selectedSource?.kind
  const state = selectedSource?.state
  const replacesLoading = selectedSource?.state === 'error' ? selectedSource.replacesLoading : undefined
  const phase = selectedSource?.kind === 'connection' ? selectedSource.phase : undefined
  const preparationPhase = selectedSource?.kind === 'preparation' ? selectedSource.phase : undefined
  const progress = selectedSource?.kind === 'preparation' ? selectedSource.progress : undefined
  const launchKind = selectedSource?.kind === 'preparation' ? selectedSource.launchKind : undefined
  if (parent.renderSources && selectedSource && effectiveVisible) {
    parent.renderSources.push(selectedSource)
  }

  const {report} = parent
  const effectiveDisplayKey = parent.acceptsChildDisplayKey ? displayKey : parent.displayKey
  useLayoutEffect(() => {
    const snapshot: SessionLoadingSource | undefined =
      kind && state
        ? kind === 'connection'
          ? state === 'error'
            ? {kind, state, replacesLoading: replacesLoading ?? false, ...(phase ? {phase} : {})}
            : {kind, state, ...(phase ? {phase} : {})}
          : kind === 'preparation'
            ? {
                kind,
                state,
                replacesLoading: replacesLoading ?? false,
                phase: preparationPhase,
                progress,
                launchKind,
              }
            : state === 'error'
              ? {kind, state, replacesLoading: replacesLoading ?? false}
              : {kind, state}
        : undefined
    report(owner, effectiveDisplayKey, snapshot, effectiveVisible, announcementTargetRef)
    return () => report(owner, effectiveDisplayKey, undefined, effectiveVisible)
  }, [
    announcementTargetRef,
    effectiveDisplayKey,
    effectiveVisible,
    kind,
    launchKind,
    owner,
    phase,
    preparationPhase,
    progress,
    replacesLoading,
    report,
    state,
  ])

  const nestedReport = useCallback<SessionLoadingReportContextValue['report']>(
    (childOwner, childDisplayKey, source, childVisible, childAnnouncementTargetRef) =>
      report(
        childOwner,
        childDisplayKey,
        source,
        effectiveVisible && childVisible,
        childAnnouncementTargetRef ?? announcementTargetRef,
      ),
    [announcementTargetRef, effectiveVisible, report],
  )

  const reportValue = useMemo<SessionLoadingReportContextValue>(
    () => ({
      displayKey: effectiveDisplayKey,
      visible: effectiveVisible,
      renderSources: parent.renderSources,
      report: nestedReport,
    }),
    [effectiveDisplayKey, effectiveVisible, nestedReport, parent.renderSources],
  )
  return <SessionLoadingReportContext value={reportValue}>{children}</SessionLoadingReportContext>
}

function SessionLoadingBoundaryRoot({
  displayKey,
  visible,
  sources,
  announcementTargetRef,
  children,
}: {
  displayKey: string
  visible: boolean
  sources: readonly SessionLoadingSource[]
  announcementTargetRef?: RefObject<HTMLElement | null>
  children: ReactNode
}) {
  const renderSourcesDuringHydration = useRenderSourcesDuringHydration()
  const [reported, setReported] = useState<{
    displayKey: string
    sources: Map<
      symbol,
      {source: SessionLoadingSource; visible: boolean; announcementTargetRef?: RefObject<HTMLElement | null>}
    >
  }>(() => ({displayKey, sources: new Map()}))
  if (reported.displayKey !== displayKey) {
    setReported({displayKey, sources: new Map()})
  }

  const report = useCallback(
    (
      owner: symbol,
      reportDisplayKey: string,
      source: SessionLoadingSource | undefined,
      sourceVisible: boolean,
      sourceAnnouncementTargetRef?: RefObject<HTMLElement | null>,
    ) => {
      if (reportDisplayKey !== displayKey) return
      setReported(current => {
        if (current.displayKey !== reportDisplayKey) return current
        const existing = current.sources.get(owner)
        if (
          sourceEqual(existing?.source, source) &&
          existing?.visible === sourceVisible &&
          existing?.announcementTargetRef === sourceAnnouncementTargetRef
        ) {
          return current
        }
        const next = new Map(current.sources)
        if (source)
          next.set(owner, {source, visible: sourceVisible, announcementTargetRef: sourceAnnouncementTargetRef})
        else next.delete(owner)
        return {displayKey: current.displayKey, sources: next}
      })
    },
    [displayKey],
  )

  const reportedSources = reported.displayKey === displayKey ? [...reported.sources.values()] : []
  const allSources = [...sources, ...reportedSources.map(reportedSource => reportedSource.source)]
  const presentationSources = [
    ...(visible ? sources : []),
    ...reportedSources.filter(reportedSource => reportedSource.visible).map(reportedSource => reportedSource.source),
  ]
  const childAnnouncementTargetRef = reportedSources.find(
    reportedSource => reportedSource.visible,
  )?.announcementTargetRef
  const stableReportValue = useMemo<SessionLoadingReportContextValue>(
    () => ({displayKey, visible, report}),
    [displayKey, report, visible],
  )
  const reportValue = useMemo<SessionLoadingReportContextValue>(
    () =>
      renderSourcesDuringHydration
        ? {...stableReportValue, renderSources: visible ? [...sources] : []}
        : stableReportValue,
    [renderSourcesDuringHydration, sources, stableReportValue, visible],
  )

  return (
    <SessionLoadingReportContext value={reportValue}>
      <SessionLoadingPresentationOwner
        displayKey={displayKey}
        visible={Boolean(selectSource(presentationSources))}
        sources={allSources}
        presentationSources={presentationSources}
        announcementTargetRef={announcementTargetRef ?? childAnnouncementTargetRef}
      >
        {children}
      </SessionLoadingPresentationOwner>
    </SessionLoadingReportContext>
  )
}

function SessionLoadingPresentationOwner({
  displayKey,
  visible,
  sources,
  presentationSources = sources,
  announcementTargetRef,
  children,
}: {
  displayKey: string
  visible: boolean
  sources: readonly SessionLoadingSource[]
  presentationSources?: readonly SessionLoadingSource[]
  announcementTargetRef?: RefObject<HTMLElement | null>
  children: ReactNode
}) {
  const descriptionId = useId()
  const hasReplacingError = sources.some(source => source.state === 'error' && source.replacesLoading)
  const pendingSource = hasReplacingError ? undefined : selectSource(sources)
  const [episode, setEpisode] = useState({active: false, displayKey, id: 0})
  let currentEpisode = episode
  if (episode.displayKey !== displayKey) {
    currentEpisode = {
      active: Boolean(pendingSource) && visible,
      displayKey,
      id: episode.id + (pendingSource && visible ? 1 : 0),
    }
    setEpisode(currentEpisode)
  } else if (!episode.active && pendingSource && visible) {
    currentEpisode = {active: true, displayKey, id: episode.id + 1}
    setEpisode(currentEpisode)
  } else if (episode.active && hasReplacingError) {
    currentEpisode = {...episode, active: false}
    setEpisode(currentEpisode)
  }
  const [slowEpisodeId, setSlowEpisodeId] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (!currentEpisode.active || pendingSource || hasReplacingError) return
    const timer = setTimeout(() => {
      setEpisode(current =>
        current.displayKey === displayKey && current.active ? {...current, active: false} : current,
      )
    }, 0)
    return () => clearTimeout(timer)
  }, [currentEpisode.active, displayKey, hasReplacingError, pendingSource])

  useEffect(() => {
    if (!currentEpisode.active) return
    const timer = setTimeout(() => setSlowEpisodeId(currentEpisode.id), SLOW_WARNING_DELAY_MS)
    return () => clearTimeout(timer)
  }, [currentEpisode.active, currentEpisode.id])

  const selectedSource = visible && !hasReplacingError ? selectSource(presentationSources) : undefined
  const hasVisibleReplacingError = presentationSources.some(
    source => source.state === 'error' && source.replacesLoading,
  )
  const slow = currentEpisode.active && slowEpisodeId === currentEpisode.id
  const baseMessage = selectedSource ? sourceMessage(selectedSource, presentationSources) : undefined
  const showSlowWarning = slow && !(selectedSource?.kind === 'preparation' && selectedSource.phase === 'cloning')
  const message = baseMessage
  const presentation = useMemo(
    () =>
      message && selectedSource
        ? {
            descriptionId,
            kind: selectedSource.kind,
            message,
            slow: showSlowWarning,
            progress:
              selectedSource.kind === 'preparation' && selectedSource.phase === 'cloning'
                ? selectedSource.progress
                : undefined,
          }
        : undefined,
    [descriptionId, message, selectedSource, showSlowWarning],
  )
  const ownsFallback = visible || hasVisibleReplacingError
  const presentationContextValue = useMemo(
    () => ({descriptionId, ownsFallback, presentation}),
    [descriptionId, ownsFallback, presentation],
  )
  const announcedMessageRef = useRef<{displayKey: string; episodeId: number; message: string} | undefined>(undefined)

  useEffect(() => {
    if (!message) return
    if (
      announcedMessageRef.current?.displayKey === displayKey &&
      announcedMessageRef.current.episodeId === currentEpisode.id &&
      announcedMessageRef.current.message === message
    ) {
      return
    }
    announcedMessageRef.current = {displayKey, episodeId: currentEpisode.id, message}
    announce(message, {
      assertive: false,
      ...(announcementTargetRef?.current ? {element: announcementTargetRef.current} : {}),
    })
  }, [announcementTargetRef, currentEpisode.id, displayKey, message])

  return (
    <SessionLoadingPresentationContext value={presentationContextValue}>{children}</SessionLoadingPresentationContext>
  )
}

export function useSessionLoadingPresentation(): SessionLoadingPresentation | undefined {
  const presentationContext = use(SessionLoadingPresentationContext)
  const reportContext = use(SessionLoadingReportContext)
  if (!presentationContext) return undefined

  const renderSources = reportContext?.renderSources
  if (!renderSources) return presentationContext.presentation
  if (renderSources.some(source => source.state === 'error' && source.replacesLoading)) {
    return undefined
  }
  const selectedSource = selectSource(renderSources)
  return selectedSource
    ? {
        descriptionId: presentationContext.descriptionId,
        kind: selectedSource.kind,
        message: sourceMessage(selectedSource, renderSources),
        slow: false,
        progress:
          selectedSource.kind === 'preparation' && selectedSource.phase === 'cloning'
            ? selectedSource.progress
            : undefined,
      }
    : undefined
}

export function useSessionLoadingPresentationOwnsFallback(): boolean {
  const presentationContext = use(SessionLoadingPresentationContext)
  const reportContext = use(SessionLoadingReportContext)
  return Boolean(presentationContext?.ownsFallback || (reportContext?.renderSources && reportContext.visible))
}

export function useSessionLoadingSource(source: SessionLoadingSource, {enabled}: {enabled: boolean}) {
  const context = use(SessionLoadingReportContext)
  if (enabled && !context) {
    throw new Error('useSessionLoadingSource requires a SessionLoadingBoundary when enabled.')
  }
  const [owner] = useState(() => Symbol('session-loading-source'))
  const report = context?.report
  const displayKey = context?.displayKey
  const visible = context?.visible
  const {kind, state} = source
  const replacesLoading = source.state === 'error' ? source.replacesLoading : undefined
  const phase = source.kind === 'connection' ? source.phase : undefined
  const preparationPhase = source.kind === 'preparation' ? source.phase : undefined
  const progress = source.kind === 'preparation' ? source.progress : undefined
  const launchKind = source.kind === 'preparation' ? source.launchKind : undefined
  const snapshot = useMemo<SessionLoadingSource>(
    () =>
      kind === 'connection'
        ? state === 'error'
          ? {kind, state, replacesLoading: replacesLoading ?? false, ...(phase ? {phase} : {})}
          : {kind, state, ...(phase ? {phase} : {})}
        : kind === 'preparation'
          ? {
              kind,
              state,
              replacesLoading: replacesLoading ?? false,
              phase: preparationPhase,
              progress,
              launchKind,
            }
          : state === 'error'
            ? {kind, state, replacesLoading: replacesLoading ?? false}
            : {kind, state},
    [kind, phase, preparationPhase, progress, launchKind, replacesLoading, state],
  )

  if (enabled && visible && context?.renderSources) {
    context.renderSources.push(snapshot)
  }

  useLayoutEffect(() => {
    if (!enabled || !report || !displayKey || visible === undefined) return
    report(owner, displayKey, snapshot, visible)
    return () => report(owner, displayKey, undefined, visible)
  }, [displayKey, enabled, owner, report, snapshot, visible])
}
